// pages/inquiry/InquiryPage.tsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createInquiry } from '../../api/deskApi';
import FileUploadComponent from "../../components/common/FileUploadComponent";

interface InquiryForm {
    title: string;
    category: string;
    content: string;
}

interface FileItem {
    id: number;
    name: string;
    file: File;
}

const InquiryPage = () => {
    const navigate = useNavigate();
    const [form, setForm] = useState<InquiryForm>({
        title: '',
        category: '',
        content: '',
    });

    const [fileList, setFileList] = useState<FileItem[]>([]);
    const [fileId, setFileId] = useState(1);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [files, setFiles] = useState<File[]>([]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setForm((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            const newFile = e.target.files[0];

            // 파일 크기 체크 (10MB)
            if (newFile.size > 10 * 1024 * 1024) {
                alert('파일 크기는 10MB 이하만 가능합니다.');
                return;
            }

            // 파일 확장자 체크
            const allowedExtensions = ['doc', 'docx', 'hwp', 'ppt', 'pptx', 'xls', 'xlsx', 'jpg', 'jpeg', 'png', 'pdf'];
            const fileExtension = newFile.name.split('.').pop()?.toLowerCase();

            if (!fileExtension || !allowedExtensions.includes(fileExtension)) {
                alert('DOC, HWP, PPT, EXCEL, JPEG, PNG, PDF 파일만 첨부 가능합니다.');
                return;
            }

            if (fileList.length < 3) {
                setFileList((prev) => [...prev, {
                    id: fileId,
                    name: newFile.name,
                    file: newFile
                }]);
                setFileId(fileId + 1);
            }
        }
        // input 초기화
        e.target.value = '';
    };

    const handleFileDelete = (id: number) => {
        setFileList((prev) => prev.filter((file) => file.id !== id));
    };

    const handleDeleteAll = () => {
        if (fileList.length > 0 && window.confirm('모든 파일을 삭제하시겠습니까?')) {
            setFileList([]);
        }
    };

    const handleAddFile = () => {
        if (fileList.length >= 3) {
            alert('파일은 최대 3개까지 첨부 가능합니다.');
            return;
        }
        document.getElementById('file-input-new')?.click();
    };

    const validateForm = (): boolean => {
        if (!form.title.trim()) {
            alert('제목을 입력해주세요.');
            return false;
        }

        if (!form.category) {
            alert('제휴유형을 선택해주세요.');
            return false;
        }

        if (!form.content.trim()) {
            alert('제안 내용을 입력해주세요.');
            return false;
        }

        return true;
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            return;
        }

        if (isSubmitting) {
            return;
        }

        if (!window.confirm('제휴문의를 등록하시겠습니까?')) {
            return;
        }

        setIsSubmitting(true);

        try {
            // FormData 직접 생성 (JSON Blob 사용 안 함)
            const formData = new FormData();
            formData.append('category', form.category);
            formData.append('title', form.title);
            formData.append('content', form.content);

            // 파일 추가
            files.forEach((file) => {
                if (file) {
                    formData.append('files', file);
                }
            });

// API 호출
            const response = await createInquiry(formData);

// 응답 처리
            if (response.data?.code === '00') {
                alert('제휴문의가 등록되었습니다.');
                navigate('/');
            }
        } catch (error: any) {
            console.error('제휴문의 등록 오류:', error);
            alert(error.response?.data?.message || '제휴문의 등록 중 오류가 발생했습니다.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            <div className="bottom-btn">
                <button
                    type="button"
                    className="btn cancel"
                    onClick={() => navigate(-1)}
                    disabled={isSubmitting}
                >
                    이전
                </button>
                <button
                    type="button"
                    className="btn confirm"
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                >
                    {isSubmitting ? '등록 중...' : '제휴 문의'}
                </button>
            </div>

            <main className="container">
                <form className="form-box">
                    <section className="bg-white">
                        <fieldset>
                            <legend>제휴문의 폼</legend>
                            <div className="inner-box">
                                <div className="form-item">
                                    <label htmlFor="title" className="input-tit">제목</label>
                                    <div className="input-box">
                                        <input
                                            type="input"
                                            id="title"
                                            name="title"
                                            value={form.title}
                                            onChange={handleInputChange}
                                            placeholder="문의 제목을 입력하세요"
                                        />
                                    </div>
                                </div>

                                <div className="form-item">
                                    <label htmlFor="category" className="input-tit">제휴유형 선택</label>
                                    <div className="input-box">
                                        <select
                                            id="category"
                                            name="category"
                                            value={form.category}
                                            onChange={handleInputChange}
                                        >
                                            <option value="" hidden>문의 유형을 선택하세요</option>
                                            <option value="SERVICE">서비스 제휴</option>
                                            <option value="BUSINESS">사업제안</option>
                                            <option value="MARKETING">마케팅</option>
                                            <option value="ETC">기타</option>
                                        </select>
                                    </div>
                                </div>

                                <div className="form-item">
                                    <label htmlFor="content" className="input-tit">제안 내용</label>
                                    <div className="input-box">
                                        <textarea
                                            id="content"
                                            name="content"
                                            value={form.content}
                                            onChange={handleInputChange}
                                        ></textarea>
                                    </div>
                                </div>

                                {/* 파일 업로드 컴포넌트 */}
                                <FileUploadComponent
                                    files={files}
                                    onFilesChange={setFiles}
                                    disabled={isSubmitting}
                                    maxFiles={3}
                                    maxSize={10}
                                />

                                {/*<div className="form-item">
                                    <label htmlFor="" className="input-tit">파일첨부
                                        <button
                                            type="button"
                                            className="delete-btn"
                                            onClick={handleDeleteAll}
                                            disabled={fileList.length === 0}
                                        >
                                            <span><i className="fa-solid fa-minus"></i></span>삭제
                                        </button>
                                        <button
                                            type="button"
                                            className="add-btn"
                                            onClick={handleAddFile}
                                        >
                                            <span><i className="fa-solid fa-plus"></i></span>추가
                                        </button>
                                    </label>

                                    {fileList.map((fileItem) => (
                                        <div key={fileItem.id} className="input-box">
                                            <div className="file-upload">
                                                <input
                                                    type="text"
                                                    className="upload-name"
                                                    value={fileItem.name}
                                                    readOnly
                                                />
                                                <input type="file" className="upload-hidden" style={{ display: 'none' }} />
                                                <button
                                                    type="button"
                                                    className="delete"
                                                    onClick={() => handleFileDelete(fileItem.id)}
                                                >
                                                    <span className="blind">삭제</span>
                                                    <i className="fa-regular fa-trash-can"></i>
                                                </button>
                                            </div>
                                        </div>
                                    ))}

                                    {fileList.length < 3 && (
                                        <div className="input-box">
                                            <div className="file-upload">
                                                <input
                                                    type="text"
                                                    className="upload-name"
                                                    value=""
                                                    placeholder="파일이 없습니다."
                                                    readOnly
                                                />
                                                <input
                                                    type="file"
                                                    id="file-input-new"
                                                    className="upload-hidden"
                                                    onChange={handleFileChange}
                                                    accept=".doc,.docx,.hwp,.ppt,.pptx,.xls,.xlsx,.jpg,.jpeg,.png,.pdf"
                                                />
                                            </div>
                                            <label htmlFor="file-input-new" className="inp-btn">
                                                <span>파일찾기</span>
                                            </label>
                                        </div>
                                    )}
                                </div>

                                <div className="guide-box mt10">
                                    <ul className="guidF-list">
                                        <li>파일은 최대 3개까지 파일 용량은 10MB이하만 가능합니다.</li>
                                        <li>파일첨부는 DOC,HWP,PPT,EXCEL,JPEG,PNG,PDF만 가능합니다.</li>
                                    </ul>
                                </div>*/}
                            </div>
                        </fieldset>
                    </section>
                </form>
            </main>
        </>
    );
};

export default InquiryPage;